import { useTranslation, t } from '../../../shared/i18n';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Alert,
    Button,
    Card,
    Col,
    Collapse,
    Form,
    Grid,
    Input,
    List,
    message,
    Modal,
    Row,
    Segmented,
    Select,
    Space,
    Spin,
    Steps,
    Tag,
    Typography,
    Result,
    theme
} from 'antd';
import {
    CopyOutlined,
    LinkOutlined,
    PlusOutlined,
    ThunderboltOutlined,
    GlobalOutlined,
    CrownOutlined,
    StarOutlined,
    CheckCircleFilled,
    CloudServerOutlined,
    ApiOutlined,
    ArrowLeftOutlined,
    ArrowRightOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import confirmDnsTakeover from './confirmDnsTakeover';
import { OwnershipTxtRecord, showOwnershipRequired } from '../../../shared/components/OwnershipTxt';
import { Pick, StepHead } from '../../../shared/components/WizardParts';
import { billingService } from '../../services/billingService';

import { userPath } from '../../../shared/config/paths';
import apiClient from '../../../shared/services/apiClient';
import { formatMoney, PLATFORM_CURRENCY } from '../../../shared/utils/money';

const { Option } = Select;
const { Text } = Typography;

// 计费周期定义（与后端 product_pricing 一致）
const BILLING_CYCLES = [
    { value: 'monthly', label: '月付', unit: '月' },
    { value: 'quarterly', label: '季付', unit: '季' },
    { value: 'semi_annually', label: '半年付', unit: '半年' },
    { value: 'annually', label: '年付', unit: '年' },
];

// 套餐/价格/配额来自 /products(与套餐页同源),不在前端硬编码。
// 配额优先取 resource_features(后端执行值),缺省回落 features(展示值)。
const PLAN_ICONS = [<GlobalOutlined />, <ThunderboltOutlined />, <StarOutlined />, <CrownOutlined />];
const PLAN_COLORS = ['#8c8c8c', '#1890ff', '#52c41a', '#faad14'];
const CACHE_LEVEL = { basic: '基础缓存', advanced: '高级缓存', full: '全功能缓存' };
const toWizardPlan = (product, idx) => {
    const f = product.features && typeof product.features === 'object' ? product.features : {};
    const rf = product.resource_features && typeof product.resource_features === 'object' ? product.resource_features : {};
    const num = (...vals) => {
        const v = vals.find((x) => x !== undefined && x !== null && x !== '' && !Number.isNaN(Number(x)));
        return v === undefined ? undefined : Number(v);
    };
    const pricing = {};
    let currency = '';
    (product.pricing || []).forEach((pr) => {
        if (pr.enabled === false || pr.enabled === 0) return;
        pricing[pr.billing_cycle] = parseFloat(pr.price);
        if (!currency && pr.currency) currency = pr.currency;
    });
    currency = currency || product.currency || PLATFORM_CURRENCY;
    const paid = Object.values(pricing).filter((v) => v > 0);
    const priceValue = paid.length ? (pricing.monthly ?? Math.min(...paid)) : 0;
    const unit = pricing.monthly != null ? t('/month') : '';
    const traffic = num(f.cdn_traffic_limit_gb);
    const hosts = num(rf.cdn_hostnames_limit, f.cdn_hostnames_per_domain_limit);
    const features = [];
    if (traffic !== undefined) features.push(traffic < 0 ? t('Unlimited traffic') : t('{{value}}/month traffic', { value: traffic >= 1000 && traffic % 1000 === 0 ? `${traffic / 1000}TB` : `${traffic}GB` }));
    if (hosts !== undefined) features.push(hosts < 0 ? t('Unlimited origin hosts') : t('{{hosts}} origin hosts', { hosts }));
    if (f.cdn_cache_level) features.push(t(CACHE_LEVEL[f.cdn_cache_level] || f.cdn_cache_level));
    const waf = f.cdn_waf_enabled === true || rf.cdn_waf_enabled === 'true' || rf.cdn_waf_enabled === true;
    if (waf) features.push('WAF');
    const i = Math.min(idx, PLAN_ICONS.length - 1);
    return {
        id: product.id,
        name: product.name,
        icon: PLAN_ICONS[i],
        color: PLAN_COLORS[i],
        price: priceValue > 0 ? `${formatMoney(priceValue, currency)}${unit}` : t('Free'),
        priceValue,
        currency,
        pricing,
        features,
        waf,
    };
};

// 新域名默认值(向导不再展示这些项,建好后在域名的 SSL/安全/网络页修改):
// SSL 开、WAF 按套餐、回源 HTTP/1.1、WebSocket 关、超时 60s。
const newDomainDefaults = (plan) => ({
    ssl_enabled: true,
    waf_enabled: !!plan?.waf,
    http_version: '1.1',
    websocket_enabled: false,
    proxy_timeout: 60,
});

// 主机名高级项留空 = 跟随域名(后端 NULL)
const emptyHostname = () => ({ hostname: '', origin: '', protocol: 'http', mode: 'roundrobin' });

// 源站地址里可带端口(地址:端口 / [IPv6]:端口);端口等于协议默认(http 80 / https 443)时不单独保存,由边缘按协议取默认
const splitOriginPort = (raw, protocol) => {
    let s = String(raw || '').trim().replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
    let port = null;
    const v6 = s.match(/^\[([^\]]+)\](?::(\d+))?$/);
    if (v6) {
        s = `[${v6[1]}]`;
        port = v6[2] ? Number(v6[2]) : null;
    } else if ((s.match(/:/g) || []).length === 1) {
        const [h, p] = s.split(':');
        if (/^\d+$/.test(p)) { s = h; port = Number(p); }
    }
    if (port === (protocol === 'https' ? 443 : 80)) port = null;
    return { origin: s, port };
};

// 用户可能粘贴 https://Example.com/path 或带结尾点
const normalizeDomain = (v) => String(v || '').trim().toLowerCase()
    .replace(/^[a-z]+:\/\//, '').replace(/[/?#].*$/, '').replace(/:\d+$/, '').replace(/\.+$/, '');

const DomainWizard = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const domainParam = searchParams.get('domain');
    const initialStep = (() => {
        const stepParam = searchParams.get('step');
        const domainParam = searchParams.get('domain');
        if (domainParam) {
            // 有域名参数时默认从套餐开始，除非显式传 step
            if (stepParam !== null) {
                const step = parseInt(stepParam, 10);
                return Number.isNaN(step) ? 1 : Math.min(Math.max(step, 0), 4);
            }
            return 1;
        }
        const step = parseInt(stepParam || '0', 10);
        if (Number.isNaN(step)) return 0;
        return Math.min(Math.max(step, 0), 4);
    })();

    const [form] = Form.useForm();
    const { token } = theme.useToken();
    const isMobile = !Grid.useBreakpoint().md;
    const watchedDomain = Form.useWatch('domain', form);
    const [currentStep, setCurrentStep] = useState(initialStep);
    const [hostnames, setHostnames] = useState([emptyHostname()]);
    const [subscription, setSubscription] = useState(null);
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);
    const [selectedSubscriptionStatus, setSelectedSubscriptionStatus] = useState(null);
    const [pendingInvoiceId, setPendingInvoiceId] = useState(null);
    const [paymentChecking, setPaymentChecking] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [createdDomainId, setCreatedDomainId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editingDomain, setEditingDomain] = useState(null);
    const [cnameInfoVisible, setCnameInfoVisible] = useState(false);
    const [currentCnameInfo, setCurrentCnameInfo] = useState(null);
    const [cnameLoading, setCnameLoading] = useState(false);
    const [accessMethod, setAccessMethod] = useState('cname');
    // 域名已托管在本账号 DNS:{hosted, ns_active, nameservers};托管的直接走 DNS 接入,不再让用户选
    const [dnsHosting, setDnsHosting] = useState(null);
    const [dnsSyncResult, setDnsSyncResult] = useState(null);
    const [dnsCheckResult, setDnsCheckResult] = useState(null);
    const [dnsCheckLoading, setDnsCheckLoading] = useState(false);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    // 2026-10-07：已购且有余量的套餐（一个套餐可覆盖多个域名）
    const [ownedSubs, setOwnedSubs] = useState([]);
    const [showOwnedSubs, setShowOwnedSubs] = useState(false);
    const [ownedSubId, setOwnedSubId] = useState(() => Number(searchParams.get('subscription_id')) || null);
    const [cdnPlans, setCdnPlans] = useState([]);

    // 主机名配额按套餐共享池算(后端同口径):池里其它域名已用 + 表单里的行数
    const [poolQuota, setPoolQuota] = useState(null);
    const quotaDomainId = editingDomain?.id || createdDomainId || null;
    useEffect(() => {
        const params = quotaDomainId ? { domain_id: quotaDomainId } : ownedSubId ? { subscription_id: ownedSubId } : null;
        if (!params) { setPoolQuota(null); return; }
        cdnService.getHostnameQuota(params).then((r) => setPoolQuota(r.success ? r.data : null));
    }, [quotaDomainId, ownedSubId]);

    const getHostnameQuota = () => {
        if (poolQuota) {
            return { limit: poolQuota.limit, used: poolQuota.used - poolQuota.domain_used + hostnames.length };
        }
        if (subscription?.resource_features?.cdn_hostnames_limit !== undefined) {
            return { limit: subscription.resource_features.cdn_hostnames_limit, used: hostnames.length };
        }
        return null;
    };

    useEffect(() => {
        const load = async () => {
            if (!domainParam) return;
            setLoading(true);
            try {
                const res = await cdnService.getDomain(domainParam);
                if (res.success && res.data) {
                    setEditingDomain(res.data);
                    setCreatedDomainId(res.data.id);
                    form.setFieldsValue({ domain: res.data.domain });
                    refreshDnsHosting(res.data.domain);
                    setSelectedSubscriptionId(res.data.subscription_id || null);
                    // 重新进入向导:恢复待付款账单,「去付款」直达该账单
                    if (res.data.invoice_id) setPendingInvoiceId(res.data.invoice_id);
                    if (res.data.group_id) {
                        setSelectedGroupId(res.data.group_id);
                    }
                    try {
                        const hostRes = await cdnService.getHostnames(res.data.id);
                        if (hostRes.success && hostRes.data?.length) {
                            setHostnames(hostRes.data);
                        }
                    } catch {
                        // ignore
                    }
                    // 如果已配置主机且订阅未支付，将流程停在 step2；否则跳到 step3
                    const hasHosts = res.data.hostnames && res.data.hostnames.length > 0;
                    if (hasHosts) {
                        setCurrentStep((s) => Math.max(s, 3));
                    }
                }
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [domainParam, form]);

    // 从 DNS 控制台「开启 CDN」跳来:预填域名和这条记录(主机名 + 源站)
    useEffect(() => {
        const name = searchParams.get('name');
        if (domainParam || !name) return;
        form.setFieldsValue({ domain: name });
        refreshDnsHosting(name);
        const host = searchParams.get('hostname');
        if (host) {
            setHostnames([{ ...emptyHostname(), hostname: host, origin: searchParams.get('origin') || '' }]);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        loadSubscription();
    }, [editingDomain]);

    useEffect(() => {
        if (accessMethod !== 'dns') {
            setDnsSyncResult(null);
        }
    }, [accessMethod]);

    useEffect(() => {
        apiClient.get('/products', { params: { product_type: 'cdn', status: 'active' } })
            .then((res) => {
                const list = (res.data?.success && Array.isArray(res.data.data) ? res.data.data : [])
                    .filter((p) => !p.is_addon)
                    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id);
                setCdnPlans(list.map(toWizardPlan));
            })
            .catch(() => message.error(t('Failed to load plan')));
    }, []);

    useEffect(() => {
        cdnService.getMySubscriptions().then((r) => {
            const list = (r.data || []).filter((s) => s.bindable);
            setOwnedSubs(list);
            const pre = ownedSubId && list.find((s) => s.id === ownedSubId);
            if (pre) setSelectedPlanId(pre.product_id);
            else if (ownedSubId) setOwnedSubId(null);
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // 当前选中分组（用于展示 CNAME 目标提示）
    const runDnsCheck = async () => {
        const baseDomain = form.getFieldValue('domain') || currentCnameInfo?.domain || (hostnames[0]?.hostname || '');
        const targetDomain = accessMethod === 'dns'
            ? baseDomain
            : ((currentCnameInfo?.hostnames?.[0]?.hostname) || baseDomain);

        if (!targetDomain) {
            message.info(t('Please enter a domain and save the origin configuration first'));
            return;
        }

        setDnsCheckLoading(true);
        try {
            const resp = await cdnService.getDnsCheck({
                domain: targetDomain,
                access_method: accessMethod,
                nameserver: '1.1.1.1'
            });
            if (resp.success) {
                setDnsCheckResult(resp.data);
            } else {
                message.error(resp.error || t('DNS query failed'));
            }
        } catch (error) {
            message.error(error.error || error.message || t('DNS query failed'));
        } finally {
            setDnsCheckLoading(false);
        }
    };

    const loadSubscription = async () => {
        try {
            // 优先使用域名绑定的订阅信息
            if (editingDomain?.subscription_id) {
                const subRes = await billingService.getSubscription(editingDomain.subscription_id);
                if (subRes.success && subRes.data) {
                    const sub = subRes.data;
                    setSubscription(sub);
                    setSelectedSubscriptionId(sub.id);
                    setSelectedSubscriptionStatus(sub.status);
                    if (sub.product_id) {
                        setSelectedPlanId(sub.product_id);
                    }
                    const paidActive = ['active', 'processing'].includes(sub.status || '') && (sub.product_price || sub.price || 0) > 0;
                    if (paidActive) {
                        setCurrentStep((s) => Math.max(s, 2));
                    }
                    return;
                }
            }

            // 退回总览（按资源）以便首次创建时提示状态
            const response = await billingService.getSubscriptionOverview('cdn', editingDomain?.id);
            if (response.success) {
                setSubscription(response.data);
                if (response.data.subscription_id) {
                    setSelectedSubscriptionId(response.data.subscription_id);
                }
                if (response.data.status) {
                    setSelectedSubscriptionStatus(response.data.status);
                }
                if (response.data.product_id) {
                    setSelectedPlanId(response.data.product_id);
                }
                const paidActive = response.data.is_paid_plan && ['active', 'processing'].includes(response.data.status || '');
                if (paidActive) {
                    setCurrentStep((s) => Math.max(s, 2));
                } else if ((response.data.is_paid_plan && response.data.status !== 'active') && currentStep < 1) {
                    setCurrentStep(1);
                } else if (!response.data.is_paid_plan && response.data.status === 'active') {
                    setCurrentStep((s) => Math.max(s, 2));
                }
            }
        } catch (error) {
            console.error('Failed to load subscription:', error);
        }
    };

    const resolvePlan = useMemo(() => {
        if (selectedPlanId) {
            return cdnPlans.find((p) => p.id === selectedPlanId);
        }
        if (subscription?.product_id) {
            return cdnPlans.find((p) => p.id === subscription.product_id);
        }
        return null;
    }, [selectedPlanId, subscription, cdnPlans]);

    // 只有已存在且绑定了生效中付费套餐的域名才锁定套餐选择;建好之前可在已购/新购之间随意切换
    const planLocked = !!(editingDomain?.id || createdDomainId)
        && ['active', 'processing'].includes(selectedSubscriptionStatus || '')
        && (resolvePlan?.priceValue > 0 || !!subscription?.is_paid_plan || (!!ownedSubId && selectedSubscriptionId === ownedSubId));

    const pickOwnedPlan = (s) => {
        if (planLocked) return;
        if (ownedSubId === s.id) {
            setOwnedSubId(null);
            setSelectedPlanId(null);
            return;
        }
        setOwnedSubId(s.id);
        setSelectedPlanId(s.product_id);
    };

    const pickNewPlan = (plan) => {
        if (planLocked) return;
        setOwnedSubId(null);
        setSelectedPlanId(plan.id);
        // 尚未建域名时选中的已购订阅状态全部清掉
        if (!createdDomainId && !editingDomain) {
            setSelectedSubscriptionId(null);
            setSelectedSubscriptionStatus(null);
            setPendingInvoiceId(null);
        }
    };

    // 当前选中套餐支持的计费周期
    const availableCycles = useMemo(() => {
        if (!resolvePlan || !resolvePlan.pricing) return [];
        return BILLING_CYCLES.filter((c) => resolvePlan.pricing[c.value] != null);
    }, [resolvePlan]);

    // 选中套餐 + 选中周期对应的价格
    const currentCyclePrice = useMemo(() => {
        if (!resolvePlan || !resolvePlan.pricing) return null;
        return resolvePlan.pricing[billingCycle] ?? resolvePlan.pricing.monthly ?? null;
    }, [resolvePlan, billingCycle]);

    // 下单用的周期必须是该套餐真有的定价周期:免费版只有 onetime,发 monthly 会被后端拒(该计费周期不可用)
    const cycleFor = (plan) => {
        const pricing = plan?.pricing || {};
        if (pricing[billingCycle] != null) return billingCycle;
        return BILLING_CYCLES.find((c) => pricing[c.value] != null)?.value || Object.keys(pricing)[0] || billingCycle;
    };

    // 切换套餐时，如新套餐不支持当前周期，回退到月付
    useEffect(() => {
        if (availableCycles.length && !availableCycles.some((c) => c.value === billingCycle)) {
            setBillingCycle(availableCycles[0].value);
        }
    }, [availableCycles]); // eslint-disable-line react-hooks/exhaustive-deps

    const refreshBillingStatus = async () => {
        const plan = resolvePlan;
        if (plan && plan.priceValue === 0 && !selectedSubscriptionId) {
            setSelectedSubscriptionStatus('active');
            return 'active';
        }

        if (!selectedSubscriptionId) {
            message.warning(t('Please submit the creation request first, then refresh after the plan is created'));
            return null;
        }

        setPaymentChecking(true);
        try {
            if (pendingInvoiceId) {
                const invoiceResp = await billingService.getInvoice(pendingInvoiceId);
                if (invoiceResp.success && invoiceResp.data?.status === 'paid') {
                    setSelectedSubscriptionStatus('processing');
                }
            }

            const resp = await billingService.getSubscription(selectedSubscriptionId);
            if (resp.success && resp.data) {
                setSelectedSubscriptionStatus(resp.data.status);
                if (!selectedPlanId && resp.data.product_id) {
                    setSelectedPlanId(resp.data.product_id);
                }
                return resp.data.status;
            }
        } catch (error) {
            message.error(error.error || error.message || t('Failed to refresh payment status'));
        } finally {
            setPaymentChecking(false);
        }
        return null;
    };

    const refreshDnsHosting = async (name) => {
        if (!name) return;
        const r = await cdnService.getDnsHosting(name);
        if (r.success) {
            setDnsHosting({ ...r.data, name });
            if (r.data?.hosted) setAccessMethod('dns');
        }
    };

    // 第 1 步输入域名时就查是否已托管在本平台 DNS(停止输入 0.5 秒后)
    useEffect(() => {
        if (currentStep !== 0) return undefined;
        const name = normalizeDomain(watchedDomain);
        if (!name || !name.includes('.')) return undefined;
        const timer = setTimeout(() => refreshDnsHosting(name), 500);
        return () => clearTimeout(timer);
    }, [watchedDomain, currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

    // 完成页:非 DNS 托管且归属未验证 → 同时给出 TXT 记录
    const [ownership, setOwnership] = useState(null);
    useEffect(() => {
        const id = editingDomain?.id || createdDomainId;
        if (currentStep !== 4 || accessMethod === 'dns' || !id) return;
        cdnService.getDomain(id).then((r) => setOwnership(r.success && r.data?.verified === false ? r.data : null));
    }, [currentStep, accessMethod, createdDomainId, editingDomain?.id]);

    // 接入方式在第 1 步就定了(已托管 = DNS,不再让选):旧的第 3 步直接越过
    useEffect(() => {
        if (currentStep === 2) setCurrentStep(3);
    }, [currentStep]);
    const hostingKnown = dnsHosting && dnsHosting.name === normalizeDomain(watchedDomain || editingDomain?.domain);
    // 域名已在我们 DNS 托管:接入方式固定为 DNS(重新进入向导直接到第 3 步时也一样),不出 CNAME
    useEffect(() => {
        if (hostingKnown && dnsHosting.hosted && accessMethod !== 'dns') setAccessMethod('dns');
    }, [hostingKnown, dnsHosting, accessMethod]);

    const addHostname = () => {
        const quota = getHostnameQuota();
        if (quota) {
            const { limit, used } = quota;
            if (limit > -1 && used >= limit) {
                Modal.warning({
                    title: t('Origin hostname limit reached'),
                    content: t('Your current plan supports up to {{limit}} origin hostnames. Please upgrade your plan or delete an existing hostname before adding another.', { limit }),
                });
                return;
            }
        }

        setHostnames([...hostnames, emptyHostname()]);
    };

    const removeHostname = (index) => {
        if (hostnames.length > 1) {
            setHostnames(hostnames.filter((_, i) => i !== index));
        } else {
            message.warning(t('At least one host configuration must be retained'));
        }
    };

    const updateHostname = (index, field, value) => {
        const newHostnames = [...hostnames];
        newHostnames[index][field] = value;
        setHostnames(newHostnames);
    };

    const ensureDomainOrder = async (plan) => {
        const values = await form.validateFields(['domain']);
        const domainName = values.domain;

        // 若已存在域名，允许重新绑定套餐（升级/降级）。只有查询本身容错;更新失败的真实原因要报给用户。
        let existing = null;
        try {
            existing = await cdnService.getDomain(domainName);
        } catch {
            existing = null;
        }
        {
            if (existing?.success && existing.data?.id) {
                const updRes = await cdnService.updateDomain({
                    id: existing.data.id,
                    product_id: plan.id,
                    billing_cycle: cycleFor(plan),
                    ...(selectedGroupId ? { group_id: selectedGroupId } : {})
                });
                if (!updRes.success) {
                    throw new Error(updRes.error || t('Failed to update plan'));
                }
                const subId = updRes.data?.subscription_id || existing.data.subscription_id;
                const invId = updRes.data?.invoice_id || null;
                setCreatedDomainId(existing.data.id);
                if (subId) setSelectedSubscriptionId(subId);
                if (invId) setPendingInvoiceId(invId);
                return { domainId: existing.data.id, subscriptionId: subId, invoiceId: invId };
            }
        }

        const res = await cdnService.createDomain({
            domain: domainName,
            ...newDomainDefaults(plan),
            hostnames: [],
            product_id: plan.id,
            billing_cycle: cycleFor(plan),
            ...(selectedGroupId ? { group_id: selectedGroupId } : {})
        });
        if (!res.success) {
            const err = new Error(res.error || t('Failed to create domain'));
            // 名字被他人未验证占用:弹 TXT 记录(结构化字段)
            err.ownershipShown = showOwnershipRequired(res);
            throw err;
        }

        setCreatedDomainId(res.data?.id);
        if (res.data?.subscription_id) {
            setSelectedSubscriptionId(res.data.subscription_id);
        }
        if (res.data?.invoice_id) {
            setPendingInvoiceId(res.data.invoice_id);
            setSelectedSubscriptionStatus('pending');
        } else {
            setSelectedSubscriptionStatus('active');
        }

        return { domainId: res.data?.id, subscriptionId: res.data?.subscription_id, invoiceId: res.data?.invoice_id };
    };

    // 绑定到已有套餐：建域名并挂到该订阅（已存在的域名走 /plan/attach），直接进入下一步
    const applyOwnedPlan = async (sub) => {
        let values;
        try {
            values = await form.validateFields(['domain']);
        } catch {
            message.error(t('Please enter basic information, including the domain, first'));
            setCurrentStep(0);
            return;
        }
        setSubmitLoading(true);
        try {
            let domainId = editingDomain?.id || createdDomainId || null;
            if (!domainId) {
                const existing = await cdnService.getDomain(values.domain).catch(() => null);
                if (existing?.success && existing.data?.id) domainId = existing.data.id;
            }
            if (domainId) {
                if (!sub.primary_id) throw new Error(t('Binding failed'));
                const r = await apiClient.post('/api/user/plan/attach', { type: 'cdn', primary_id: sub.primary_id, to_id: domainId }, { skipGlobalErrorToast: true })
                    .then((x) => x.data)
                    .catch((err) => err?.response?.data || { success: false, error: err?.message });
                if (!r?.success) throw new Error(r?.error || t('Binding failed'));
                setCreatedDomainId(domainId);
            } else {
                const r = await cdnService.createDomain({
                    domain: values.domain,
                    ...newDomainDefaults(cdnPlans.find((p) => p.id === sub.product_id)),
                    hostnames: [],
                    subscription_id: sub.id,
                    ...(selectedGroupId ? { group_id: selectedGroupId } : {})
                });
                if (!r.success) throw new Error(r.error || t('Failed to create domain'));
                setCreatedDomainId(r.data?.id);
            }
            setOwnedSubId(sub.id);
            setSelectedPlanId(sub.product_id);
            setSelectedSubscriptionId(sub.id);
            setSelectedSubscriptionStatus('active');
            setPendingInvoiceId(null);
            setCurrentStep(2);
        } catch (e) {
            message.error(e.message || t('Operation failed'));
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleNextStep = async () => {
        try {
            await form.validateFields(['domain']);
            if (currentStep === 0) {
                const clean = normalizeDomain(form.getFieldValue('domain'));
                form.setFieldsValue({ domain: clean });
                refreshDnsHosting(clean);
                setCurrentStep(1);
                return;
            }

            // 已绑定(锁定)或已挂到所选已购套餐:不再重复下单/绑定
            if (currentStep === 1 && (planLocked || (ownedSubId && selectedSubscriptionId === ownedSubId))) {
                setCurrentStep(2);
                return;
            }

            if (currentStep === 1 && ownedSubId && ownedSubs.some((s) => s.id === ownedSubId) && selectedSubscriptionId !== ownedSubId) {
                await applyOwnedPlan(ownedSubs.find((s) => s.id === ownedSubId));
                return;
            }

            if (currentStep === 1) {
                const plan = resolvePlan;
                if (!plan) {
                    message.error(t('Select a plan to continue'));
                    return;
                }
                setSubmitLoading(true);
                try {
                    const orderInfo = await ensureDomainOrder(plan);

                    if (plan.priceValue > 0) {
                        const latestStatus = await refreshBillingStatus();
                        const effectiveStatus = latestStatus || selectedSubscriptionStatus || 'pending';
                        if (!['active', 'processing'].includes(effectiveStatus)) {
                            const invoiceId = orderInfo.invoiceId || pendingInvoiceId;
                            Modal.confirm({
                                title: t('Pay for the plan first'),
                                content: t('A paid plan has been created for this domain. Please complete payment before continuing setup.'),
                                okText: t('Pay now'),
                                cancelText: t('Later'),
                                onOk: () => {
                                    if (invoiceId) {
                                        navigate(userPath(`/billing/invoices?id=${invoiceId}`));
                                    } else {
                                        navigate(userPath('/billing/invoices'));
                                    }
                                }
                            });
                            return;
                        }
                    }

                    setCurrentStep(2);
                } catch (error) {
                    if (!error.ownershipShown) message.error(error.message || t('Failed to create plan/domain'));
                } finally {
                    setSubmitLoading(false);
                }
                return;
            }

            if (currentStep === 2) {
                if (!accessMethod) {
                    message.error(t('Select a setup method'));
                    return;
                }
                setCurrentStep(3);
            }
        } catch {
            message.error(t('Please enter basic information, including the domain, first'));
        }
    };

    const syncHostnames = async (domainId, hosts, extra = {}) => {
        const cur = await cdnService.getHostnames(domainId);
        const byName = new Map((cur.success ? cur.data || [] : []).map((h) => [String(h.hostname).toLowerCase(), h]));
        const failed = [];
        for (const host of hosts) {
            const ex = byName.get(host.hostname);
            const same = ex && String(ex.origin) === String(host.origin) && Number(ex.port || 0) === Number(host.port || 0)
                && (ex.protocol || 'http') === host.protocol && (ex.mode || 'roundrobin') === host.mode;
            const res = same ? { success: true }
                : ex ? await cdnService.updateHostname({ id: ex.id, ...host })
                    : await cdnService.createHostname({ domain_id: domainId, ...host, ...extra });
            if (!res.success) failed.push(`${host.hostname}: ${res.error || t('Operation failed')}`);
        }
        return failed;
    };

    const handleSubmit = async () => {
        let values;
        try {
            values = { domain: form.getFieldValue('domain') || editingDomain?.domain };
            if (!values.domain) throw Object.assign(new Error(''), { errorFields: [1] });
        } catch (error) {
            if (error.errorFields) {
                message.error(t('Please complete all basic information'));
            } else {
                message.error(t('Form validation failed: {{message}}', { message: error.message }));
            }
            return;
        }

        const preparedHostnames = hostnames
            .filter((h) => (h.origin || '').trim())
            .map((h) => ({
                hostname: (h.hostname && h.hostname.trim()) || values.domain,
                ...splitOriginPort(h.origin, h.protocol || 'http'),
                protocol: h.protocol || 'http',
                mode: h.mode || 'roundrobin',
                // 留空 = 跟随域名(编辑已有主机名时保留其原值)
                http_version: h.http_version || null,
                websocket_enabled: h.websocket_enabled ?? null,
                proxy_timeout: h.proxy_timeout || null
            }));

        if (preparedHostnames.length === 0) {
            message.error(t('At least one origin address is required'));
            return;
        }
        const seenHosts = new Set();
        for (const h of preparedHostnames) {
            h.hostname = h.hostname.toLowerCase();
            if (seenHosts.has(h.hostname)) {
                message.error(t('Duplicate hostname: {{name}}', { name: h.hostname }));
                return;
            }
            seenHosts.add(h.hostname);
        }

        const planForSubmit = resolvePlan;
        // 已有订阅(含已下架的旧套餐)不要求在售套餐;只有全新建域名才需要
        if (!planForSubmit && !selectedSubscriptionId) {
            message.error(t('Select a plan before submitting'));
            setCurrentStep(1);
            return;
        }

        if (!selectedSubscriptionId) {
            message.error(t('Please create a plan/domain in step 2 before submitting the origin'));
            setCurrentStep(1);
            return;
        }

        const data = {
            ...values,
            hostnames: preparedHostnames,
            subscription_id: selectedSubscriptionId,
            ...(planForSubmit ? { product_id: planForSubmit.id, billing_cycle: cycleFor(planForSubmit) } : {}),
            access_method: accessMethod,
            ...(selectedGroupId ? { group_id: selectedGroupId } : {})
        };

        // 托管 DNS 里已有这些名字的解析:加入后改成走 CDN,先确认
        if (!(await confirmDnsTakeover(preparedHostnames.map((h) => h.hostname)))) return;
        data.confirm_dns = 1;

        if (editingDomain) {
            data.id = editingDomain.id;
            // 套餐已在第 2 步定好;重复提交不能再带套餐参数(否则订阅过期时会静默下新单)
            delete data.product_id;
            delete data.billing_cycle;
        }

        setSubmitLoading(true);

        try {
            data.subscription_id = selectedSubscriptionId;

            let response;
            let domainIdCreatedNow = null;
            const existingDomainId = editingDomain?.id || createdDomainId || null;

            if (editingDomain) {
                response = await cdnService.updateDomain({ ...data, id: editingDomain.id });
            } else if (existingDomainId) {
                response = { success: true, data: { id: existingDomainId, domain: values.domain } };
            } else {
                response = await cdnService.createDomain({ ...(planForSubmit ? newDomainDefaults(planForSubmit) : {}), ...data });
                if (response.success && response.data?.id) {
                    domainIdCreatedNow = response.data.id;
                }
            }

            if (response.success) {
                const invoiceId = response.data?.invoice_id || pendingInvoiceId;
                if (response.data?.subscription_id) {
                    setSelectedSubscriptionId(response.data.subscription_id);
                }
                if (response.data?.id) {
                    setCreatedDomainId(response.data.id);
                }
                if (invoiceId) {
                    setPendingInvoiceId(invoiceId);
                }

                if (!editingDomain && planForSubmit?.priceValue > 0 && invoiceId) {
                    message.info(t('Domain and order created. Redirecting to the invoice to complete payment.'));
                    navigate(userPath(`/billing/invoices?id=${invoiceId}`));
                    return;
                }

                // 域名在本次提交之前就存在(重新进入向导 / 第 1 步已建):逐个同步主机名 ——
                // 已有的更新、新的创建;单个失败不影响其余,也不会让重试卡在"已存在"。
                const targetDomainId = response.data?.id || existingDomainId || domainIdCreatedNow;
                if (targetDomainId && !domainIdCreatedNow) {
                    const failed = await syncHostnames(targetDomainId, preparedHostnames, { confirm_dns: 1 });
                    if (failed.length) {
                        throw new Error(failed.join('; '));
                    }
                }

                // 域名在第 1 步已建好,这里走的是"已有域名"分支,后端不会自动建 DNS 记录;NS 接入需显式触发一次
                let dnsSync = response.data?.dns_sync || null;
                if (accessMethod === 'dns' && !dnsSync && targetDomainId) {
                    try {
                        const provRes = await cdnService.updateDomain({ id: targetDomainId, access_method: 'dns' });
                        dnsSync = provRes?.data?.dns_sync || (provRes?.success ? null : { success: false, error: provRes?.error });
                    } catch (e) {
                        dnsSync = { success: false, error: e?.error || e?.message };
                    }
                }

                const hostnameList = (response.data?.hostnames || []).map((h) => ({
                    key: h.id || h.hostname,
                    hostname: h.hostname,
                    cnameTarget: h.cname_target
                }));

                if (hostnameList.length > 0) {
                    setCurrentCnameInfo({
                        domain: response.data?.domain || values.domain,
                        hostnames: hostnameList
                    });
                } else if (response.data) {
                    await fetchCnameInfo({ id: response.data.id, domain: response.data.domain || values.domain });
                }

                setDnsSyncResult(dnsSync);

                if (accessMethod === 'dns') {
                    await refreshDnsHosting(response.data?.domain || values.domain);
                }

                message.success(editingDomain ? t('Updated successfully. Setup guide generated.') : t('Created successfully. Setup instructions are ready.'));
                setCurrentStep(4);
            } else {
                message.error(response.error || t('Operation failed'));
            }
        } catch (error) {
            message.error(t('Operation failed: {{message}}', { message: error.message }));
        } finally {
            setSubmitLoading(false);
        }
    };

    const fetchCnameInfo = async (domainRecord) => {
        if (!domainRecord?.id) {
            message.info(t('Please save the domain and origin configuration first'));
            return;
        }

        setCnameLoading(true);
        try {
            const response = await cdnService.getHostnames(domainRecord.id);
            if (response.success) {
                const hostnameList = (response.data || []).map((h) => ({
                    key: h.id || h.hostname,
                    hostname: h.hostname,
                    cnameTarget: h.cname_target
                }));

                setCurrentCnameInfo({
                    domain: domainRecord.domain,
                    hostnames: hostnameList
                });
                if (accessMethod === 'cname') {
                    setCnameInfoVisible(true);
                }
            } else {
                message.error(response.error || t('Failed to retrieve CNAME information'));
            }
        } catch (error) {
            message.error(t('Failed to retrieve CNAME information: {{message}}', { message: error.message }));
        } finally {
            setCnameLoading(false);
        }
    };

    const renderCnameModalBody = () => {
        if (cnameLoading) {
            return (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Spin />
                </div>
            );
        }

        if (!currentCnameInfo) {
            return (
                <Alert
                    message={t('No CNAME information yet')}
                    type="info"
                    showIcon
                />
            );
        }

        const list = currentCnameInfo.hostnames || [];

        return (
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                <Alert
                    message={t('DNS setup guide')}
                    description={t('Add CNAME records for the following hosts to complete setup. Built-in DNS syncs automatically, with no manual action needed.')}
                    type="info"
                    showIcon
                />

                <Space>
                    <Text strong>{t('Domain:')}</Text>
                    <Tag color="blue">{currentCnameInfo.domain}</Tag>
                </Space>

                {list.length === 0 ? (
                    <Alert
                        message={t('No origin configured yet')}
                        description={t('Please add the hosts you want to accelerate in Origin settings first, then view their CNAME records.')}
                        type="warning"
                        showIcon
                    />
                ) : (
                    <List
                        dataSource={list}
                        renderItem={(item) => (
                            <List.Item
                                actions={[
                                    item.cnameTarget ? (
                                        <Button
                                            type="link"
                                            size="small"
                                            icon={<CopyOutlined />}
                                            onClick={() => navigator.clipboard.writeText(item.cnameTarget)}
                                        >{t('Copy')}</Button>
                                    ) : null
                                ]}
                            >
                                <List.Item.Meta
                                    title={<Text strong>{item.hostname}</Text>}
                                    description={item.cnameTarget ? <Text code>{item.cnameTarget}</Text> : <Text type="warning">{t('Access address is being generated; refresh shortly')}</Text>}
                                />
                            </List.Item>
                        )}
                    />
                )}
            </Space>
        );
    };

    const renderDnsCheckResult = () => {
        if (!dnsCheckResult) return null;
        const records = dnsCheckResult.records || {};
        return (
            <Card title={t('DNS check results')} size="small">
                {Object.keys(records).map((type) => {
                    const val = records[type];
                    if (val?.error) {
                        return <Alert key={type} type="error" message={t('{{type}} lookup failed', { type })} description={val.error} showIcon />;
                    }
                    if (!val || val.length === 0) {
                        return <Alert key={type} type="warning" message={t('No {{type}} records yet', { type })} showIcon />;
                    }
                    return (
                        <List
                            key={type}
                            size="small"
                            header={<Space><Text strong>{type}</Text>{dnsCheckResult.matched && type in dnsCheckResult.matched && (
                                dnsCheckResult.matched[type]
                                    ? <Tag color="green">{t('Active')}</Tag>
                                    : <Tag color="orange">{t('Not pointing to {{value}}', { value: [].concat(dnsCheckResult.expected?.[type] || []).join(', ') })}</Tag>
                            )}</Space>}
                            dataSource={val}
                            renderItem={(item) => (
                                <List.Item>
                                    <Text>{item}</Text>
                                </List.Item>
                            )}
                        />
                    );
                })}
            </Card>
        );
    };

    // 展示用 4 步(内部仍是 0~4,第 2 步「接入方式」已并入第 1 步)
    const viewStep = [0, 1, 1, 2, 3][currentStep] ?? 0;
    const shownDomain = normalizeDomain(watchedDomain || editingDomain?.domain);
    const domainHosted = hostingKnown && dnsHosting.hosted;

    return (
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
            <Button type="text" icon={<ArrowLeftOutlined />} style={{ marginBottom: 12 }} onClick={() => navigate(userPath('/cdn/domains'))}>{t('Back to list')}</Button>

            <Card styles={{ body: { padding: 0 } }} style={{ overflow: 'hidden' }}>
                <div style={{
                    padding: isMobile ? '20px 16px' : '28px 40px',
                    background: `linear-gradient(135deg, ${token.colorPrimaryBg} 0%, ${token.colorBgContainer} 70%)`,
                    borderBottom: `1px solid ${token.colorBorderSecondary}`,
                }}>
                    <Typography.Title level={3} style={{ margin: '0 0 20px' }}>
                        {shownDomain && currentStep > 0 ? shownDomain : t('Add a site to CDN')}
                    </Typography.Title>
                    <Steps
                        current={viewStep}
                        responsive
                        items={[
                            { title: t('Domain'), description: currentStep > 0 ? (domainHosted ? 'Yeap DNS' : (accessMethod === 'dns' ? t('NS setup') : t('CNAME setup'))) : null },
                            { title: t('Select plan'), description: viewStep > 1 ? (resolvePlan?.name || null) : null },
                            { title: t('Origin settings') },
                            { title: t('Done') },
                        ]}
                    />
                </div>

                <div style={{ padding: isMobile ? '24px 16px' : '36px 40px', minHeight: 320 }}>
                {currentStep === 0 && (
                    <div style={{ maxWidth: 720 }}>
                        <StepHead title={t('Which domain do you want to accelerate?')} desc={t('Enter the root domain. Subdomains are added in the origin step.')} />
                        <Form form={form} layout="vertical">
                            <Form.Item name="domain" rules={[{ required: true, message: t('Please enter a domain') }]}>
                                <Input
                                    size="large"
                                    prefix={<GlobalOutlined style={{ color: token.colorTextTertiary }} />}
                                    placeholder="example.com"
                                    disabled={!!editingDomain || !!createdDomainId}
                                    onPressEnter={handleNextStep}
                                    style={{ fontSize: 18, padding: '10px 14px' }}
                                />
                            </Form.Item>
                        </Form>
                        {domainHosted && (
                            <div style={{
                                display: 'flex', gap: 14, alignItems: 'flex-start', padding: 18,
                                borderRadius: token.borderRadiusLG, background: token.colorSuccessBg, border: `1px solid ${token.colorSuccessBorder}`,
                            }}>
                                <CheckCircleFilled style={{ color: token.colorSuccess, fontSize: 22, marginTop: 2 }} />
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 15 }}>{t('This domain is on Yeap DNS')}</div>
                                    <Text type="secondary">
                                        {dnsHosting.ns_active
                                            ? t('DNS records are added automatically. No DNS changes needed.')
                                            : t('DNS records are added automatically and take effect once the nameservers point to Yeap DNS.')}
                                    </Text>
                                </div>
                            </div>
                        )}
                        {hostingKnown && !dnsHosting.hosted && (
                            <>
                                <Text strong style={{ display: 'block', marginBottom: 12 }}>{t('Setup method')}</Text>
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} sm={12}>
                                        <Pick active={accessMethod === 'cname'} onClick={() => setAccessMethod('cname')} icon={<ApiOutlined />}
                                            title={t('CNAME setup')} desc={t('Keep your current DNS provider and add a CNAME record there.')} />
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Pick active={accessMethod === 'dns'} onClick={() => setAccessMethod('dns')} icon={<CloudServerOutlined />}
                                            title={t('NS setup')} desc={t('Move DNS to Yeap DNS; records are managed for you.')} />
                                    </Col>
                                </Row>
                            </>
                        )}
                    </div>
                )}

                {currentStep === 1 && (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <StepHead title={t('Choose a plan')} desc={t('One plan can cover several domains; you can upgrade at any time.')} />
                        {/* 挂到已购套餐不常用:默认收起,点开才显示 */}
                        {ownedSubs.length > 0 && !showOwnedSubs && !ownedSubId && (
                            <Button type="link" style={{ padding: 0, alignSelf: 'flex-start' }} onClick={() => setShowOwnedSubs(true)}>
                                {t('Use an existing plan')}
                            </Button>
                        )}
                        {ownedSubs.length > 0 && (showOwnedSubs || !!ownedSubId) && (
                            <Card size="small" title={t('Bind to an existing plan')}>
                                <Space wrap>
                                    {ownedSubs.map((s) => (
                                        <Button
                                            key={s.id}
                                            type={ownedSubId === s.id ? 'primary' : 'default'}
                                            loading={submitLoading && ownedSubId === s.id}
                                            disabled={planLocked && ownedSubId !== s.id}
                                            onClick={() => pickOwnedPlan(s)}
                                        >{s.name || `#${s.id}`} · {s.product_name}{Number(s.limit) !== 1 ? ` · ${t('Used {{used}}/', { used: s.used })}${s.limit < 0 ? t('Unlimited') : s.limit}` : ''}
                                        </Button>
                                    ))}
                                </Space>
                            </Card>
                        )}
                        <Card size="small" title={ownedSubs.length > 0 && (showOwnedSubs || !!ownedSubId) ? t('Buy a new plan') : undefined}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Row gutter={[16, 16]}>
                                    {cdnPlans.map((plan) => {
                                        const isSelected = !ownedSubId && selectedPlanId === plan.id;
                                        const disabled = planLocked && !isSelected;
                                        return (
                                            <Col xs={24} sm={12} key={plan.id}>
                                                <Card
                                                    bordered
                                                    style={{
                                                        borderColor: isSelected ? plan.color : undefined,
                                                        borderWidth: isSelected ? 2 : 1,
                                                        opacity: disabled ? 0.6 : 1,
                                                        pointerEvents: disabled ? 'none' : 'auto'
                                                    }}
                                                    title={
                                                        <Space>
                                                            {plan.icon}
                                                            <Text strong>{plan.name}</Text>
                                                            {plan.tag && <Tag color={plan.color}>{plan.tag}</Tag>}
                                                        </Space>
                                                    }
                                                    extra={<Tag color={plan.color}>{plan.price}</Tag>}
                                                    onClick={() => { if (!disabled) pickNewPlan(plan); }}
                                                    hoverable
                                                >
                                                    <List
                                                        size="small"
                                                        dataSource={plan.features}
                                                        renderItem={(item) => <List.Item style={{ padding: '4px 0' }}>• {item}</List.Item>}
                                                    />
                                                    <Button
                                                        type={isSelected ? 'primary' : 'default'}
                                                        block
                                                        style={{ marginTop: 12 }}
                                                        disabled={disabled}
                                                    >
                                                        {isSelected ? t('Selected') : t('Select a plan')}
                                                    </Button>
                                                </Card>
                                            </Col>
                                        );
                                    })}
                                </Row>
                                {!ownedSubId && resolvePlan && resolvePlan.priceValue > 0 && availableCycles.length > 0 && (
                                    <div>
                                        <Text strong style={{ marginRight: 12 }}>{t('Billing cycle')}</Text>
                                        <Segmented
                                            value={billingCycle}
                                            onChange={setBillingCycle}
                                            disabled={planLocked}
                                            options={availableCycles.map((c) => ({
                                                label: t('{{label}} ({{price}}/{{unit}})', { label: t(c.label), price: formatMoney(resolvePlan.pricing[c.value], resolvePlan.currency), unit: t(c.unit) }),
                                                value: c.value,
                                            }))}
                                        />
                                        {currentCyclePrice != null && (
                                            <Text type="secondary" style={{ marginLeft: 12 }}>{t('Total {{currentCyclePrice}}', { currentCyclePrice: formatMoney(currentCyclePrice, resolvePlan.currency) })}</Text>
                                        )}
                                    </div>
                                )}
                                {!ownedSubId && resolvePlan && resolvePlan.priceValue > 0 && !selectedSubscriptionId && !pendingInvoiceId && (
                                    <Alert
                                        type="info"
                                        showIcon
                                        message={t('Current selection: {{name}}', { name: resolvePlan.name })}
                                        description={t('Click "Create order and continue" below to create an unpaid order. You can continue configuration after completing payment.')}
                                    />
                                )}
                                {!ownedSubId && resolvePlan && resolvePlan.priceValue > 0 && (selectedSubscriptionId || pendingInvoiceId) && (
                                    <Alert
                                        type={['active', 'processing'].includes(selectedSubscriptionStatus) ? 'success' : 'warning'}
                                        showIcon
                                        message={t('Current selection: {{name}}', { name: resolvePlan.name })}
                                        description={
                                            ['active', 'processing'].includes(selectedSubscriptionStatus) ? (
                                                <Text>{t('Your plan is active. You can continue setup.')}</Text>
                                            ) : (
                                                <Space direction="vertical" style={{ width: '100%' }}>
                                                    <Text>{t('Order created (awaiting payment). Please complete payment to continue.')}</Text>
                                                    <Space>
                                                        <Button
                                                            type="primary"
                                                            onClick={() => {
                                                                if (pendingInvoiceId) {
                                                                    navigate(userPath(`/billing/invoices?id=${pendingInvoiceId}`));
                                                                } else {
                                                                    navigate(userPath('/billing/invoices'));
                                                                }
                                                            }}
                                                        >{t('Pay now')}</Button>
                                                        <Button
                                                            onClick={refreshBillingStatus}
                                                            loading={paymentChecking}
                                                        >{t('I\'ve paid, refresh status')}</Button>
                                                        <Text type="secondary">{t('Status:')}{selectedSubscriptionStatus || t('Payment pending')}
                                                        </Text>
                                                    </Space>
                                                </Space>
                                            )
                                        }
                                    />
                                )}
                            </Space>
                        </Card>
                    </Space>
                )}

                {currentStep === 3 && (
                    <div>
                        <StepHead title={t('Where is your origin?')} desc={t('Add each hostname to serve and the server the CDN fetches it from.')} />
                        {hostnames.map((hostname, index) => (
                            <Card
                                key={index}
                                size="small"
                                style={{ marginBottom: 16 }}
                                title={t('Host configuration {{value}}', { value: index + 1 })}
                                extra={
                                    hostnames.length > 1 && (
                                        <Button
                                            type="link"
                                            danger
                                            size="small"
                                            onClick={() => removeHostname(index)}
                                        >{t('Delete')}</Button>
                                    )
                                }
                            >
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item label={t('Hostname')}>
                                            <Input
                                                value={hostname.hostname}
                                                onChange={(e) => updateHostname(index, 'hostname', e.target.value)}
                                                placeholder="www.example.com"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item label={t('Origin address')} tooltip={t('IP or domain; for a non-standard port use address:port')}>
                                            <Input
                                                value={hostname.origin}
                                                onChange={(e) => updateHostname(index, 'origin', e.target.value)}
                                                placeholder="origin.example.com"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item label={t('Protocol')}>
                                            <Select
                                                value={hostname.protocol}
                                                onChange={(value) => updateHostname(index, 'protocol', value)}
                                            >
                                                <Option value="http">HTTP</Option>
                                                <Option value="https">HTTPS</Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Collapse
                                    ghost
                                    size="small"
                                    items={[{
                                        key: 'adv',
                                        label: t('Advanced configuration'),
                                        children: (
                                            <Row gutter={16}>
                                                <Col xs={24} sm={12}>
                                                    <Form.Item label={t('Load mode')}>
                                                        <Select
                                                            value={hostname.mode}
                                                            onChange={(value) => updateHostname(index, 'mode', value)}
                                                        >
                                                            <Option value="roundrobin">{t('Round robin')}</Option>
                                                            <Option value="ip_hash">IP Hash</Option>
                                                            <Option value="least_conn">{t('Least connections')}</Option>
                                                        </Select>
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        ),
                                    }]}
                                />
                            </Card>
                        ))}

                        <Button
                            type="dashed"
                            onClick={addHostname}
                            block
                            icon={<PlusOutlined />}
                        >{t('Add host configuration')}</Button>
                    </div>
                )}

                {currentStep === 4 && (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Result
                            status="success"
                            style={{ padding: '8px 0 0' }}
                            title={t('Site added')}
                            subTitle={accessMethod === 'dns'
                                ? ((dnsSyncResult?.ns_active ?? dnsHosting?.ns_active) ? t('DNS records were added automatically. No further action is needed.') : t('Change the nameservers below to finish.'))
                                : t('Add the CNAME records below at your DNS provider to finish.')}
                        />
                        {accessMethod === 'dns' ? (
                            <Space direction="vertical" style={{ width: '100%' }}>
                                {dnsSyncResult && !dnsSyncResult.success && (
                                    <Alert type="error" showIcon message={t('Failed to create DNS records automatically')} description={dnsSyncResult.error} />
                                )}
                                {(dnsSyncResult?.ns_active ?? dnsHosting?.ns_active) ? null : (
                                    <Card
                                        title={t('Change your nameservers to')}
                                        extra={<Button size="small" loading={dnsCheckLoading} onClick={runDnsCheck}>{t('DNS check')}</Button>}
                                    >
                                        <List
                                            dataSource={dnsSyncResult?.nameservers?.length ? dnsSyncResult.nameservers : (dnsHosting?.nameservers || [])}
                                            locale={{ emptyText: t('Loading...') }}
                                            renderItem={(ns) => (
                                                <List.Item actions={[<Button type="link" size="small" icon={<CopyOutlined />} onClick={() => navigator.clipboard.writeText(ns)}>{t('Copy')}</Button>]}>
                                                    <Text code>{ns}</Text>
                                                </List.Item>
                                            )}
                                        />
                                    </Card>
                                )}
                                {(dnsSyncResult?.ns_active ?? dnsHosting?.ns_active) ? null : renderDnsCheckResult()}
                        </Space>
                    ) : (
                            <Card title={t('CNAME configuration')}>
                                {(!currentCnameInfo || (currentCnameInfo.hostnames || []).length === 0) ? (
                                    <Alert message={t('No CNAME information yet. Please make sure the origin has been saved.')} type="warning" showIcon />
                                ) : (
                                    <List
                                        dataSource={currentCnameInfo.hostnames}
                                        renderItem={(item) => (
                                            <List.Item
                                                actions={[
                                                    item.cnameTarget ? (
                                                        <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => navigator.clipboard.writeText(item.cnameTarget)}>{t('Copy')}</Button>
                                                    ) : null
                                                ]}
                                            >
                                                <List.Item.Meta
                                                    title={<Text strong>{item.hostname}</Text>}
                                                    description={item.cnameTarget ? <Text code>{item.cnameTarget}</Text> : <Text type="warning">{t('Access address is being generated; refresh shortly')}</Text>}
                                                />
                                            </List.Item>
                                        )}
                                    />
                                )}
                            </Card>
                        )}
                        {accessMethod !== 'dns' && ownership?.txt_name && (
                            <Card size="small" title={t('Verify domain ownership')}>
                                <OwnershipTxtRecord name={ownership.txt_name} value={ownership.txt_value} />
                            </Card>
                        )}
                    </Space>
                )}

                </div>

                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
                    padding: isMobile ? '14px 16px' : '16px 40px',
                    borderTop: `1px solid ${token.colorBorderSecondary}`, background: token.colorFillQuaternary,
                }}>
                    <div>
                        {currentStep > 0 && currentStep < 4 && (
                            <Button size="large" icon={<ArrowLeftOutlined />} onClick={() => setCurrentStep(currentStep === 3 ? 1 : currentStep - 1)}>{t('Previous step')}</Button>
                        )}
                    </div>
                    <Space>
                        {currentStep < 3 && (
                            <Button size="large" type="primary" onClick={handleNextStep} loading={submitLoading}>
                                {currentStep === 1
                                    && !ownedSubId
                                    && resolvePlan?.priceValue > 0
                                    && !['active', 'processing'].includes(selectedSubscriptionStatus || '')
                                    ? t('Create order and continue')
                                    : t('Next step')}
                                <ArrowRightOutlined />
                            </Button>
                        )}
                        {currentStep === 3 && (
                            <Button size="large" type="primary" onClick={handleSubmit} loading={submitLoading}>{t('Submit')}</Button>
                        )}
                        {currentStep === 4 && (
                            <Button size="large" type="primary" onClick={() => navigate(userPath('/cdn/domains'))}>{t('Done')}</Button>
                        )}
                    </Space>
                </div>
            </Card>

            <Modal
                title={<Space><LinkOutlined />{t(' CNAME setup guide')}</Space>}
                open={cnameInfoVisible}
                onCancel={() => {
                    setCnameInfoVisible(false);
                }}
                footer={[
                    <Button
                        key="close"
                        onClick={() => {
                            setCnameInfoVisible(false);
                        }}
                    >{t('Close')}</Button>
                ]}
                width={760}
            >
                {renderCnameModalBody()}
            </Modal>
        </div>
    );
};

export default DomainWizard;
